import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Home,
  Search,
  Plus,
  Heart,
  User,
  Bell,
  LogOut,
  Package,
  ShoppingBag,
  MessageCircle,
  Menu,
  X,
  HelpCircle,
  Mail,
  LayoutDashboard,
  Check,
  ExternalLink,
  Clock,
  Moon,
  Sun,
  Sunset,
  DollarSign,
  ChevronDown,
  Tag,
  ListTodo,
  Boxes,
  Bookmark,
  Crown,
  Sparkles,
  Download,
  PawPrint,
  Car,
  Coins,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { WelcomeModal } from './WelcomeModal';
import { PWAUpdateBanner } from './PWAUpdateBanner';
import { PWAInstallBanner } from './PWAInstallBanner';
import { OfflineBanner } from './OfflineBanner';

export const Layout = ({ children, hideFooter = false }) => {
  const { user, logout, unreadCount, unreadMessagesCount, api, installPrompt, fetchNotifications: refreshUnreadCounts } = useAuth();
  const { theme, toggleTheme, isDark, isEvening, isBlackDark } = useTheme();
  const navigate = useNavigate();
  const currentPath = useLocation().pathname;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showInstallDialog, setShowInstallDialog] = useState(false);

  // Mobile / install detection (computed once — stable for the session)
  const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 768;
  const isAppInstalled = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
  const isIOSDevice = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  // Show the menu item whenever on mobile and not already installed.
  // Don't gate on installPrompt — the event may not have fired yet.
  const showInstallMenuItem = isMobileDevice && !isAppInstalled;

  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const welcomeShownThisSession = useRef(false);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  
  // Messages/Conversations state
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  
  // Menu counts state
  const [menuCounts, setMenuCounts] = useState({
    wants: 0,
    inventory: 0,
    matches: 0,
    newMatches: 0
  });

  // Points balance — shown inline in the profile dropdown
  const [pointsBalance, setPointsBalance] = useState(null);

  // Theme icon based on current theme
  const ThemeIcon = theme === 'light' ? Sun : theme === 'evening' ? Sunset : Moon;
  const themeTitle = theme === 'light' ? 'Light Mode' : theme === 'evening' ? 'Evening Mode' : 'Dark Mode';

  // Fetch menu counts on mount and user change
  useEffect(() => {
    const fetchMenuCounts = async () => {
      if (!user) return;
      try {
        // Fetch counts in parallel
        const [wantsRes, inventoryRes, matchesRes] = await Promise.all([
          api.get('/wtb/my').catch(() => ({ data: [] })),
          api.get('/inventory/my').catch(() => ({ data: [] })),
          api.get('/matches/').catch(() => ({ data: [] }))
        ]);
        
        const matches = matchesRes.data || [];
        const newMatches = matches.filter(m => 
          m.status === 'auto_matched' && 
          new Date(m.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) // Last 7 days
        ).length;
        
        setMenuCounts({
          wants: wantsRes.data?.length || 0,
          inventory: inventoryRes.data?.length || 0,
          matches: matches.length,
          newMatches: newMatches
        });
      } catch (error) {
        console.error('Failed to fetch menu counts:', error);
      }
    };
    
    fetchMenuCounts();
  }, [user, api]);

  // Fetch points balance for profile dropdown display
  useEffect(() => {
    if (!user) { setPointsBalance(null); return; }
    let cancelled = false;
    api.get('/points/balance')
      .then(r => { if (!cancelled) setPointsBalance(r.data.points_balance ?? 0); })
      .catch(() => { if (!cancelled) setPointsBalance(0); });
    return () => { cancelled = true; };
  }, [user, api]);

  // Refetch unread counts on navigation into pages that mark items read server-side,
  // so the header badges clear immediately instead of waiting for the next poll.
  // Small delay lets the server-side mark-as-read finish before we re-poll the count.
  useEffect(() => {
    if (!user) return;
    if (currentPath.startsWith('/notifications') || currentPath.startsWith('/messages')) {
      const t = setTimeout(() => refreshUnreadCounts(), 500);
      return () => clearTimeout(t);
    }
  }, [currentPath, user, refreshUnreadCounts]);

  // Check if we should show welcome modal (first login for users with active trial)
  useEffect(() => {
    if (!user || !user.is_trial_active || user.trial_days_remaining <= 0) return;
    if (welcomeShownThisSession.current) return; // already triggered this mount
    const welcomeModalKey = `wantd_welcome_shown_${user.id}`;
    if (localStorage.getItem(welcomeModalKey)) return; // already seen ever
    welcomeShownThisSession.current = true;
    // Show welcome modal after a short delay for smoother UX
    const timer = setTimeout(() => {
      setShowWelcomeModal(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [user]);

  const handleCloseWelcomeModal = () => {
    if (user) {
      const welcomeModalKey = `wantd_welcome_shown_${user.id}`;
      localStorage.setItem(welcomeModalKey, 'true');
    }
    setShowWelcomeModal(false);
  };

  const fetchNotifications = async () => {
    if (!user || notificationsLoading) return;
    setNotificationsLoading(true);
    try {
      const response = await api.get('/notifications/?limit=5');
      setNotifications(response.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const fetchConversations = async () => {
    if (!user || conversationsLoading) return;
    setConversationsLoading(true);
    try {
      const response = await api.get('/messages/recent-conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.account_type === 'owner') return '/my-items';
    if (user.account_type === 'wanter') return '/my-wants';
    return '/my-wants';
  };

  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Plus, label: 'Create', path: user?.account_type === 'owner' ? '/list' : '/post-want' },
    { icon: User, label: 'Profile', path: '/profile' },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--theme-bg)' }}>
      {/* Welcome Modal for new users with active trial */}
      {showWelcomeModal && user && (
        <WelcomeModal user={user} onClose={handleCloseWelcomeModal} />
      )}
      
      {/* Suspension Banner */}
      {user?.suspension_status && user.suspension_status.tier !== 'permanent' && user.suspension_status.expires_at && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-red-600 text-white text-center py-2 px-4 text-sm font-medium">
          Your account is suspended until {new Date(user.suspension_status.expires_at).toLocaleString('en-AU')}. You cannot create listings or send messages during this period.
        </div>
      )}

      {/* Desktop Navigation */}
      <nav className={`hidden md:block nav-glass fixed ${user?.suspension_status && user.suspension_status.tier !== 'permanent' && user.suspension_status.expires_at ? 'top-10' : 'top-0'} left-0 right-0 z-50`}>
        <div className="w-full px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left Side - Logo + Nav Links */}
            <div className="flex items-center gap-8">
              {/* Logo with tagline - Different versions for each theme */}
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src={theme === 'light' ? '/logo-wantd-navy.png' : '/logo-wantd-white.png'}
                  alt="WANTD" 
                  className="h-8 w-auto"
                />
              </Link>

              {/* Nav Links */}
              <div className="flex items-center gap-5">
                <Link
                  to="/"
                  className={`text-sm font-medium transition-colors ${currentPath === '/' ? 'text-[var(--theme-text-primary)]' : 'text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]'}`}
                >
                  Catalog
                </Link>
                {user && (
                  <>
                    {/* BUY & SELL Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid="nav-buysell-dropdown"
                          className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                            ['/my-wants', '/post-want', '/my-items', '/list', '/list/item', '/list/photos', '/saved'].includes(currentPath)
                              ? 'text-[var(--theme-text-primary)]'
                              : 'text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]'
                          }`}
                        >
                          Buy &amp; Sell
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="bg-[var(--theme-card)] border-[var(--theme-border)] w-48">
                        <DropdownMenuItem
                          onClick={() => navigate('/list')}
                          className="cursor-pointer hover:bg-[var(--theme-hover)]"
                        >
                          <Tag className="w-4 h-4 mr-2 text-[#059669]" />
                          <span className="text-[var(--theme-text-primary)]">List an Item</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/post-want')}
                          className="cursor-pointer hover:bg-[var(--theme-hover)]"
                        >
                          <ShoppingBag className="w-4 h-4 mr-2 text-[#C9956C]" />
                          <span className="text-[var(--theme-text-primary)]">Post a Want</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/my-items')}
                          className="cursor-pointer hover:bg-[var(--theme-hover)]"
                        >
                          <Package className="w-4 h-4 mr-2 text-[#1E3A5F]" />
                          <span className="text-[var(--theme-text-primary)]">My Items</span>
                          {menuCounts.inventory > 0 && (
                            <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full bg-[var(--theme-hover)] text-[var(--theme-text-muted)]">
                              {menuCounts.inventory}
                            </span>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/saved')}
                          className="cursor-pointer hover:bg-[var(--theme-hover)]"
                        >
                          <Bookmark className="w-4 h-4 mr-2 text-[#1E3A5F]" />
                          <span className="text-[var(--theme-text-primary)]">Saved Items</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* WANTD Auto shortcut — icon expands to label on hover */}
                    <button
                      onClick={() => navigate('/auto')}
                      className="flex items-center gap-1 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors group"
                    >
                      <Car className="w-4 h-4 flex-shrink-0" />
                      <span className="max-w-0 overflow-hidden group-hover:max-w-[6rem] transition-[max-width] duration-200 whitespace-nowrap text-sm font-medium">
                        WANTD Auto
                      </span>
                    </button>

                    {/* WANTD Pets shortcut — icon expands to label on hover */}
                    <button
                      onClick={() => navigate('/pets')}
                      className="flex items-center gap-1 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors group"
                    >
                      <PawPrint className="w-4 h-4 flex-shrink-0" />
                      <span className="max-w-0 overflow-hidden group-hover:max-w-[6rem] transition-[max-width] duration-200 whitespace-nowrap text-sm font-medium">
                        WANTD Pets
                      </span>
                    </button>

                  </>
                )}
              </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              {user ? (
                <>
                  {/* Messages Dropdown - Badge shows unread messages count */}
                  <DropdownMenu onOpenChange={(open) => open && fetchConversations()}>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className="relative p-2 rounded-full hover:bg-[var(--theme-hover)] transition-colors"
                        title="Messages"
                      >
                        <Mail className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                        {unreadMessagesCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#DC2626] rounded-full text-xs flex items-center justify-center text-white font-bold">
                            {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                          </span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[var(--theme-card)] border-[var(--theme-border)] w-80">
                      <div className="px-3 py-2 flex items-center justify-between border-b border-[var(--theme-border)]">
                        <span className="font-bold text-[var(--theme-text-primary)]">Messages</span>
                        {unreadMessagesCount > 0 && (
                          <span className="text-xs text-[var(--theme-text-secondary)]">{unreadMessagesCount} unread</span>
                        )}
                      </div>
                      
                      {conversationsLoading ? (
                        <div className="px-3 py-6 text-center">
                          <div className="animate-spin w-5 h-5 border-2 border-[var(--theme-border)] border-t-[#C9956C] rounded-full mx-auto"></div>
                        </div>
                      ) : conversations.length > 0 ? (
                        <div className="max-h-64 overflow-y-auto">
                          {conversations.map((conv) => (
                            <DropdownMenuItem
                              key={conv.match_id}
                              onClick={() => navigate(`/messages/${conv.match_id}`)}
                              className="cursor-pointer hover:bg-[var(--theme-hover)] px-3 py-3"
                            >
                              <div className="flex items-start gap-3 w-full">
                                <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                                  {conv.other_username?.[0]?.toUpperCase() || 'U'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-[var(--theme-text-primary)] text-sm">{conv.other_username || 'User'}</span>
                                    <span className="text-xs text-[var(--theme-text-muted)]">{formatTimeAgo(conv.last_message?.created_at)}</span>
                                  </div>
                                  <p className="text-xs text-[var(--theme-text-secondary)] truncate">{conv.item_title}</p>
                                  <p className="text-sm text-[var(--theme-text-secondary)] truncate mt-0.5">
                                    {conv.last_message?.sender_id === user.id ? 'You: ' : ''}{conv.last_message?.content}
                                  </p>
                                </div>
                                {conv.unread_count > 0 && (
                                  <span className="w-5 h-5 bg-[#DC2626] rounded-full text-xs flex items-center justify-center text-white font-bold flex-shrink-0">
                                    {conv.unread_count}
                                  </span>
                                )}
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </div>
                      ) : (
                        <div className="px-3 py-6 text-center">
                          <Mail className="w-8 h-8 text-[var(--theme-text-muted)] mx-auto mb-2" />
                          <p className="text-sm text-[var(--theme-text-secondary)]">No messages yet</p>
                        </div>
                      )}
                      
                      <DropdownMenuSeparator className="bg-[var(--theme-border)]" />
                      <DropdownMenuItem
                        onClick={() => navigate('/messages')}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] justify-center"
                      >
                        <span className="text-[#C9956C] text-sm font-medium flex items-center gap-1">
                          Go to All Messages
                          <ExternalLink className="w-3 h-3" />
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Notifications Dropdown - Shows non-message notifications only */}
                  <DropdownMenu onOpenChange={(open) => open && fetchNotifications()}>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className="relative p-2 rounded-full hover:bg-[var(--theme-hover)] transition-colors" 
                        title="Notifications"
                      >
                        <Bell className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C9956C] rounded-full text-xs flex items-center justify-center text-white font-bold">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[var(--theme-card)] border-[var(--theme-border)] w-80">
                      <div className="px-3 py-2 flex items-center justify-between border-b border-[var(--theme-border)]">
                        <span className="font-bold text-[var(--theme-text-primary)]">Notifications</span>
                        {unreadCount > 0 && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); markAllNotificationsRead(); }}
                            className="text-xs text-[#C9956C] hover:text-[#B8845B] font-medium"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                      
                      {notificationsLoading ? (
                        <div className="px-3 py-6 text-center">
                          <div className="animate-spin w-5 h-5 border-2 border-[var(--theme-border)] border-t-[#C9956C] rounded-full mx-auto"></div>
                        </div>
                      ) : notifications.length > 0 ? (
                        <div className="max-h-80 overflow-y-auto">
                          {notifications.map((notif) => (
                            <DropdownMenuItem
                              key={notif.id}
                              onClick={() => {
                                if (!notif.is_read) markNotificationRead(notif.id);
                                if (notif.data?.match_id) navigate(`/matches/${notif.data.match_id}`);
                              }}
                              className={`cursor-pointer hover:bg-[var(--theme-hover)] px-3 py-3 ${!notif.is_read ? 'bg-[var(--theme-hover)]' : ''}`}
                            >
                              <div className="flex items-start gap-3 w-full">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  notif.type === 'match_found' ? 'bg-[#C9956C]/10' :
                                  notif.type === 'message' ? 'bg-[#1E3A5F]/10' :
                                  notif.type === 'match_unlocked' ? 'bg-[#059669]/10' :
                                  'bg-[var(--theme-hover)]'
                                }`}>
                                  {notif.type === 'match_found' && <Heart className="w-5 h-5 text-[#C9956C]" />}
                                  {notif.type === 'message' && <MessageCircle className={`w-5 h-5 ${isDark ? 'text-[#C9956C]' : 'text-[#1E3A5F]'}`} />}
                                  {notif.type === 'match_unlocked' && <Check className="w-5 h-5 text-[#059669]" />}
                                  {!['match_found', 'message', 'match_unlocked'].includes(notif.type) && <Bell className="w-5 h-5 text-[var(--theme-text-secondary)]" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className={`text-sm ${!notif.is_read ? 'font-bold text-[var(--theme-text-primary)]' : 'font-medium text-[var(--theme-text-primary)]'}`}>
                                      {notif.title}
                                    </span>
                                    {!notif.is_read && (
                                      <span className="w-2 h-2 bg-[#C9956C] rounded-full flex-shrink-0"></span>
                                    )}
                                  </div>
                                  <p className="text-sm text-[var(--theme-text-secondary)] line-clamp-2">{notif.message}</p>
                                  <div className="flex items-center gap-1 mt-1 text-xs text-[var(--theme-text-muted)]">
                                    <Clock className="w-3 h-3" />
                                    {formatTimeAgo(notif.created_at)}
                                  </div>
                                </div>
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </div>
                      ) : (
                        <div className="px-3 py-6 text-center">
                          <Bell className="w-8 h-8 text-[var(--theme-text-muted)] mx-auto mb-2" />
                          <p className="text-sm text-[var(--theme-text-secondary)]">No notifications yet</p>
                          <p className="text-xs text-[var(--theme-text-muted)] mt-1">We&apos;ll notify you of matches & messages</p>
                        </div>
                      )}
                      
                      <DropdownMenuSeparator className="bg-[var(--theme-border)]" />
                      <DropdownMenuItem
                        onClick={() => navigate('/notifications')}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] justify-center"
                      >
                        <span className="text-[#C9956C] text-sm font-medium flex items-center gap-1">
                          View All Notifications
                          <ExternalLink className="w-3 h-3" />
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Upgrade Button - Free tier only */}
                  {!user.is_premium && !user.is_trial_active && (
                    <Link
                      to="/premium"
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#C9956C] hover:bg-[#B8845B] text-white text-xs font-semibold transition-colors"
                      title="Upgrade to WANTD+"
                    >
                      <Crown className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Upgrade</span>
                    </Link>
                  )}

                  {/* Profile Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className={`relative flex items-center gap-2 p-2 rounded-full hover:bg-[var(--theme-hover)] transition-colors ${
                        (user.is_premium || user.is_trial_active) ? 'ring-2 ring-[#C9956C] ring-offset-2 ring-offset-[var(--theme-bg)]' : ''
                      }`}>
                        {/* Premium glow effect */}
                        {(user.is_premium || user.is_trial_active) && (
                          <div className="absolute inset-0 rounded-full bg-[#C9956C]/20 animate-pulse" />
                        )}
                        {user.profile_image ? (
                          <img 
                            src={user.profile_image.startsWith('/uploads/') 
                              ? `${process.env.REACT_APP_BACKEND_URL}/api${user.profile_image}` 
                              : user.profile_image.startsWith('/api/') 
                                ? `${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`
                                : user.profile_image}
                            alt={user.username}
                            className="w-8 h-8 rounded-full object-cover relative z-10"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div 
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold relative z-10 ${
                            (user.is_premium || user.is_trial_active) 
                              ? 'bg-[#C9956C] text-white' 
                              : isDark ? 'bg-[#C9956C] text-[#1E3A5F]' : 'bg-[#1E3A5F] text-white'
                          }`}
                          style={{ display: user.profile_image ? 'none' : 'flex' }}
                        >
                          {user.username[0].toUpperCase()}
                        </div>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[var(--theme-card)] border-[var(--theme-border)] w-56">
                      <div className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[var(--theme-text-primary)]">{user.username}</p>
                          {(user.is_premium || user.is_trial_active) ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#C9956C] text-white">
                              <Crown className="w-3 h-3" />
                              WANTD+
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--theme-hover)] text-[var(--theme-text-muted)]">
                              Free
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[var(--theme-text-secondary)]">{user.email}</p>
                        <div className="mt-1 trust-badge inline-flex">
                          Trust Score: {user.trust_score}
                        </div>
                      </div>
                      <DropdownMenuSeparator className="bg-[var(--theme-border)]" />
                      <DropdownMenuItem 
                        onClick={() => navigate('/dashboard')}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)]"
                      >
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        My Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate('/profile')}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)]"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Profile Settings
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate('/points')}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)]"
                      >
                        <Coins className="w-4 h-4 mr-2 text-[#C9956C]" />
                        <span className="flex-1">Points &amp; Wallet</span>
                        {pointsBalance != null && (
                          <span className="text-xs font-semibold text-[#C9956C] font-['Manrope']">
                            {pointsBalance.toLocaleString()}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => { e.preventDefault(); toggleTheme(); }}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)]"
                      >
                        <ThemeIcon className={`w-4 h-4 mr-2 ${isDark ? 'text-[#C9956C]' : ''}`} />
                        <span className="flex-1">{themeTitle}</span>
                        <span className="text-[10px] text-[var(--theme-text-muted)]">Switch</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate('/help')}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)]"
                        data-testid="help-link"
                      >
                        <HelpCircle className="w-4 h-4 mr-2" />
                        Help Centre
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[var(--theme-border)]" />
                      <DropdownMenuItem
                        onClick={() => navigate('/my-items')}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)]"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        My Items
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate('/my-wants')}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)]"
                      >
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        My Wants
                      </DropdownMenuItem>
                      {/* Admin Portal - Only visible to admins */}
                      {user?.role && ['user_admin', 'admin', 'superadmin'].includes(user.role) && (
                        <>
                          <DropdownMenuSeparator className="bg-[var(--theme-border)]" />
                          <DropdownMenuItem 
                            onClick={() => navigate('/admin')}
                            className="cursor-pointer hover:bg-purple-500/10 text-purple-600"
                          >
                            <LayoutDashboard className="w-4 h-4 mr-2" />
                            Admin Portal
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator className="bg-[var(--theme-border)]" />
                      <DropdownMenuItem 
                        onClick={handleLogout}
                        className="cursor-pointer hover:bg-[var(--theme-hover)] text-red-500"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <div className="flex items-center">
                    <span className="text-sm text-[#C9956C] font-medium mr-1 animate-pulse">
                      Read me
                    </span>
                    <span className="text-[#C9956C] mr-1">→</span>
                    <Link 
                      to="/help" 
                      className="p-2 rounded-full hover:bg-[var(--theme-hover)] transition-colors"
                      title="Help Center"
                    >
                      <HelpCircle className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                    </Link>
                  </div>
                  <Link to="/login">
                    <Button variant="ghost" className="text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)]">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className={`rounded-full px-6 ${isDark ? 'bg-[#C9956C] text-[#1E3A5F] hover:bg-[#B8845B]' : 'bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white'}`}>
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Header */}
      <nav className="md:hidden nav-glass fixed top-0 left-0 right-0 z-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={theme === 'light' ? '/logo-wantd-navy.png' : '/logo-wantd-white.png'}
              alt="WANTD" 
              className="h-7 w-auto"
            />
          </Link>

          <div className="flex items-center gap-2">
            {/* Messages icon with unread badge */}
            {user && (
              <Link to="/messages" className="relative p-2">
                <Mail className="w-5 h-5 text-[#6B7280]" />
                {unreadMessagesCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#DC2626] rounded-full text-xs flex items-center justify-center text-white font-bold">
                    {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                  </span>
                )}
              </Link>
            )}
            {/* Notifications icon - non-message notifications only */}
            {user && (
              <Link to="/notifications" className="relative p-2">
                <Bell className="w-5 h-5 text-[#6B7280]" />
                {unreadCount > 0 && unreadCount !== unreadMessagesCount && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#C9956C] rounded-full text-xs flex items-center justify-center text-white font-bold">
                    {(unreadCount - unreadMessagesCount) > 9 ? '9+' : (unreadCount - unreadMessagesCount)}
                  </span>
                )}
              </Link>
            )}
            {user ? (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-[var(--theme-text-primary)]" />
                ) : (
                  <Menu className="w-6 h-6 text-[var(--theme-text-primary)]" />
                )}
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/help"
                  className="p-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]"
                  aria-label="Help Centre"
                >
                  <HelpCircle className="w-5 h-5" />
                </Link>
                <Link
                  to="/login"
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold font-['Manrope'] ${isDark ? 'bg-[#C9956C] text-[#1E3A5F]' : 'bg-[#1E3A5F] text-white'}`}
                >
                  Sign Up / Login
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-full right-0 mt-1 w-max bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg py-2 shadow-soft">
            <div className="px-2 space-y-1">
              {user ? (
                <>
                  <Link 
                    to={getDashboardLink()} 
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--theme-hover)]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className={`w-5 h-5 ${isDark ? 'text-[#C9956C]' : 'text-[#1E3A5F]'}`} />
                    <span className="text-[var(--theme-text-primary)]">My Dashboard</span>
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--theme-hover)]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                    <span className="text-[var(--theme-text-primary)]">Profile Settings</span>
                  </Link>
                  <Link
                    to="/points"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--theme-hover)]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Coins className="w-5 h-5 text-[#C9956C]" />
                    <span className="text-[var(--theme-text-primary)] flex-1">Points &amp; Wallet</span>
                    {pointsBalance != null && (
                      <span className="text-xs font-semibold text-[#C9956C] font-['Manrope']">
                        {pointsBalance.toLocaleString()}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={() => { toggleTheme(); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--theme-hover)] w-full text-left"
                  >
                    <ThemeIcon className={`w-5 h-5 ${isDark ? 'text-[#C9956C]' : 'text-[var(--theme-text-secondary)]'}`} />
                    <span className="text-[var(--theme-text-primary)] flex-1">{themeTitle}</span>
                    <span className="text-[10px] text-[var(--theme-text-muted)]">Switch</span>
                  </button>
                  <Link
                    to="/help"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--theme-hover)]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <HelpCircle className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                    <span className="text-[var(--theme-text-primary)]">Help Centre</span>
                  </Link>
                  {/* Install App — hidden once installed or on desktop */}
                  {showInstallMenuItem && (
                    <>
                      <div className="border-t border-[var(--theme-border)] my-2" />
                      <button
                        onClick={() => { setShowInstallDialog(true); setMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--theme-hover)] w-full text-left"
                      >
                        <Download className="w-5 h-5 text-[#1E3A5F]" />
                        <span className="text-[var(--theme-text-primary)]">Install App</span>
                      </button>
                    </>
                  )}
                  <div className="border-t border-[var(--theme-border)] my-2" />
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--theme-hover)] w-full text-left text-red-500"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Sign Out</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--theme-hover)]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User className={`w-5 h-5 ${isDark ? 'text-[#C9956C]' : 'text-[#1E3A5F]'}`} />
                    <span className="text-[var(--theme-text-primary)]">Sign Up / Login</span>
                  </Link>
                  <Link
                    to="/help"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--theme-hover)]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <HelpCircle className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                    <span className="text-[var(--theme-text-primary)]">Help Centre</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-20 md:pt-24 pb-24 md:pb-8 flex-grow">
        {children}
      </main>

      {/* Footer - Only show on public pages when not logged in */}
      {!user && !hideFooter && (
        <footer className="hidden md:block border-t border-[var(--theme-border)] bg-[var(--theme-card)] py-6">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-[var(--theme-text-secondary)]">
                © {new Date().getFullYear()} WANTD. All rights reserved.
              </div>
              <div className="flex items-center gap-6 text-sm">
                <Link to="/about-us" className="text-[var(--theme-text-secondary)] hover:text-[#C9956C]">
                  About Us
                </Link>
                <Link to="/help" className="text-[var(--theme-text-secondary)] hover:text-[#C9956C]">
                  Help Center
                </Link>
                <Link to="/terms" className="text-[var(--theme-text-secondary)] hover:text-[#C9956C]">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="text-[var(--theme-text-secondary)] hover:text-[#C9956C]">
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </footer>
      )}

      {/* Mobile Bottom Navigation */}
      {user && !hideFooter && (
        <div className="md:hidden bottom-nav">
          <div className="flex items-center justify-around">
            {/* Catalog */}
            <Link
              to="/"
              className={`flex flex-col items-center gap-0.5 py-1 px-2 ${
                currentPath === '/' ? 'text-[#1E3A5F]' : 'text-[var(--theme-text-muted)]'
              }`}
            >
              <Home className="w-5 h-5" />
              <span className="text-[10px]">Catalog</span>
            </Link>

            {/* Buy & Sell - Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex flex-col items-center gap-0.5 py-1 px-2 focus:outline-none ${
                  ['/marketplace', '/post-want', '/my-wants', '/list', '/my-items', '/saved'].some(p => currentPath.startsWith(p))
                    ? 'text-[#1E3A5F]'
                    : 'text-[var(--theme-text-muted)]'
                }`}>
                  <ShoppingBag className="w-5 h-5" />
                  <span className="text-[10px]">Buy &amp; Sell</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" side="top" className="mb-2 w-52">
                <DropdownMenuItem onClick={() => navigate('/list')} className="cursor-pointer py-2.5">
                  <Tag className="w-4 h-4 mr-3 text-[#059669]" />
                  <span>List an Item</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/post-want')} className="cursor-pointer py-2.5">
                  <Heart className="w-4 h-4 mr-3 text-[#C9956C]" />
                  <span>Post a Want</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/my-items')} className="cursor-pointer py-2.5">
                  <Package className="w-4 h-4 mr-3 text-[#1E3A5F]" />
                  <span>My Items</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/saved')} className="cursor-pointer py-2.5">
                  <Bookmark className="w-4 h-4 mr-3 text-[#1E3A5F]" />
                  <span>Saved Items</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* My Items shortcut */}
            <Link
              to="/my-items"
              className={`flex flex-col items-center gap-0.5 py-1 px-2 ${
                currentPath.startsWith('/my-items') || currentPath.startsWith('/my-wants') ? 'text-[#1E3A5F]' : 'text-[var(--theme-text-muted)]'
              }`}
            >
              <Package className="w-5 h-5" />
              <span className="text-[10px]">My Items</span>
            </Link>

            {/* Profile */}
            <Link
              to="/profile"
              className={`flex flex-col items-center gap-0.5 py-1 px-2 ${
                currentPath === '/profile' ? 'text-[#1E3A5F]' : 'text-[var(--theme-text-muted)]'
              }`}
            >
              <User className="w-5 h-5" />
              <span className="text-[10px]">Profile</span>
            </Link>
          </div>
        </div>
      )}

      {/* Offline status banner */}
      <OfflineBanner />

      {/* PWA update available banner */}
      <PWAUpdateBanner />

      {/* PWA install banner — shown on home, profile, and matches pages; or forced via menu */}
      <PWAInstallBanner
        show={
          currentPath === '/' ||
          currentPath === '/profile' ||
          currentPath.startsWith('/matches')
        }
        forceShow={showInstallDialog}
        onClose={() => setShowInstallDialog(false)}
      />
    </div>
  );
};
